import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Field, Toast } from '../components/ui';
import { useToast } from '../lib/hooks';

export function AccountPage() {
  const { me, reload, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!me) return null;
  const isAdmin = me.role === 'admin';
  const min = isAdmin ? 12 : 8;
  const firstAccess = me.mustChangePassword;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setMessage(null);
    if (form.next !== form.confirm) {
      setErrors({ confirm: 'As duas senhas não são iguais.' });
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(form.current, form.next);
      const user = await reload();
      setForm({ current: '', next: '', confirm: '' });
      if (firstAccess) navigate(user.role === 'admin' ? '/admin' : '/painel', { replace: true });
      else toast.show('Senha alterada');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({
          current: err.fields.currentPassword ?? '',
          next: err.fields.newPassword ?? '',
        });
        setMessage(err.message);
      } else setMessage('Não foi possível salvar. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  async function logoutEverywhere() {
    try {
      await api.logoutAll();
    } finally {
      logout();
      navigate('/login', { replace: true });
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 6 }}>
          <p className="eyebrow">&lt;_Minha_Conta/&gt;</p>
          <h1 className="t-display">{firstAccess ? 'Crie a sua senha' : 'Minha conta'}</h1>
          <p className="t-sub">
            {me.name} · {me.email}
          </p>
        </div>
      </div>

      <div className="stack-lg" style={{ maxWidth: 560 }}>
        {firstAccess && (
          <div className="notice" role="status">
            <span className="t-small">
              Você entrou com uma senha provisória. Crie uma senha só sua para continuar: ninguém da equipe vai saber qual é.
            </span>
          </div>
        )}
        {message && (
          <div className="notice notice--error" role="alert">
            {message}
          </div>
        )}

        <form className="card stack" onSubmit={submit} noValidate>
          <h2 className="t-h3">{firstAccess ? 'Nova senha' : 'Trocar senha'}</h2>
          {/* Campo oculto com o e-mail: ajuda o gerenciador de senhas a salvar no login certo. */}
          <input type="email" autoComplete="username" value={me.email} readOnly hidden />
          <Field label={firstAccess ? 'Senha provisória' : 'Senha atual'} htmlFor="acc-current" error={errors.current}>
            <input
              id="acc-current"
              className="input"
              type="password"
              autoComplete="current-password"
              value={form.current}
              onChange={set('current')}
            />
          </Field>
          <Field
            label="Nova senha"
            htmlFor="acc-next"
            error={errors.next}
            hint={`Pelo menos ${min} caracteres. Uma frase curta é mais fácil de lembrar e difícil de adivinhar.`}
          >
            <input id="acc-next" className="input" type="password" autoComplete="new-password" value={form.next} onChange={set('next')} />
          </Field>
          <Field label="Repita a nova senha" htmlFor="acc-confirm" error={errors.confirm}>
            <input
              id="acc-confirm"
              className="input"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={set('confirm')}
            />
          </Field>
          <div>
            <button className="btn btn--action" type="submit" disabled={busy || !form.current || !form.next}>
              {busy ? 'Salvando…' : 'Salvar senha'}
            </button>
          </div>
          <p className="t-caption">Ao trocar a senha, as sessões abertas em outros aparelhos são encerradas.</p>
        </form>

        {!firstAccess && (
          <section className="card stack">
            <h2 className="t-h3">Sessões</h2>
            <p className="muted t-small">
              Esqueceu o painel aberto em outro computador ou celular? Saia de todos os aparelhos de uma vez, inclusive deste.
            </p>
            <div>
              <button className="btn btn--ghost" type="button" onClick={logoutEverywhere}>
                Sair de todos os dispositivos
              </button>
            </div>
          </section>
        )}
      </div>
      <Toast message={toast.message} />
    </>
  );
}
